import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

type StorageMethod = 'set' | 'get' | 'clear';

interface StorageParams {
  method: StorageMethod;
  key: string;
  value?: any;
}


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const pluralDays = (count: number) => `${count} day${count === 1 ? '' : 's'}`;


export const customLocalStorage = ({ method, key, value }: StorageParams) => {
  const storedItem = localStorage.getItem(key);
  switch (method) {
    case 'set':
      if (value !== undefined) {
        localStorage.setItem(key, JSON.stringify(value));
      }
      break;

    case 'get':
      return storedItem ? JSON.parse(storedItem) : null;

    case 'clear':
      localStorage.removeItem(key);
      break;

    default:
      throw new Error('Invalid method');
  }
};

export const navbarOptions = [
  { id: 'problem', name: 'The Reality' },
  { id: 'partner', name: 'Our Support' },
  { id: 'plan', name: 'The Plan' },
  { id: 'reviews', name: 'Success Stories' },
];

export const loadPersistedData = (key: string, defaultValue: any) => {
  const storedData = localStorage?.getItem(key);

  if (storedData) {
    try {
      return JSON.parse(storedData);
    } catch {
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
};

// Only same-app relative paths (single leading '/') are safe post-login redirect targets —
// a protocol-relative path ('//host/...') resolves to a different origin.
export function isSafeRedirectPath(path?: string): path is string {
  // Browsers normalise a backslash or control character ('/\\evil' -> '//evil').
  return (
    !!path &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    ![...path].some(char => char === '\\' || char.charCodeAt(0) < 0x20)
  );
}

export const generateSessionId = () => {
  let sessionId = '';
  while (sessionId.length < 27) {
    sessionId += Math.random().toString(36).substring(2);
  }
  return sessionId.substring(0, 27);
};

// `crypto.randomUUID` needs Chrome 92+ / Safari 15.4+, and some collectors'
// phones are older; `getRandomValues` is the same CSPRNG and works on all of them.
export const generateIdempotencyKey = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export function capitalizeFirstLetter(str: string) {
  return str.replace(/^([a-z])/, match => match.toUpperCase());
}

export const getErrorMessage = (
  error:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined
) => {
  if (typeof error === 'string') return error;
  if (error && typeof error.message === 'string') return error.message;
  return null;
};

export const processErrors = (errorData: any, setError: any) => {
  if (errorData) {
    const keys = Object.keys(errorData).filter(key => key !== 'details');
    keys.forEach(key => {
      let errorMessage = errorData[key];

      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage[0];
      }
      setError(key, {
        type: 'server',
        message: errorMessage,
      });
    });
  }
};

export function getEnumDisplayTextLegacyDoNotUseMe(
  enums: { label: string; value: string }[] | null | undefined,
  value: string
): string | undefined {
  if (!enums) {
    return value;
  }
  const enumItem = enums.find(enumItem => enumItem.value === value);
  return enumItem ? enumItem.label : value;
}
export function getEnumDisplayText(
  enums: { [key: string]: string } | undefined,
  value: string
): string | undefined {
  if (!enums || !value) return undefined;
  return enums[value];
}

export const truncateText = (text: string, limit: number) => {
  if (text && text.length > limit) {
    return text.substring(0, limit) + '...';
  }
  return text;
};

export const stripHtmlTags = (html: string): string => {
  const stripOnce = (value: string): string => {
    const withoutStyle = value.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    const parser = new DOMParser();
    return (
      parser.parseFromString(withoutStyle, 'text/html').body.textContent || ''
    );
  };

  // Strip tags and decode entities in a loop, not once — text extracted from
  // an uploaded document can be multi-level-encoded (a literal "&amp;lt;" that
  // only becomes markup once unescaped), matching the backend's iterative
  // sanitize_plain_text (skuulbe-api's html_sanitizer.py).
  let value = html || '';
  for (let i = 0; i < 3; i++) {
    const stripped = stripOnce(value);
    if (stripped === value) return stripped;
    value = stripped;
  }
  return value;
};

export const formatNumberWithThousandSeparator = (
  amount: number | string
): string => {
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;
  return isNaN(numericAmount)
    ? ''
    : new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numericAmount);
};

// Money with exactly two decimals and en-US separators, whatever the device
// locale, matching the printed receipt: 1500 -> "1,500.00".
const moneyFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// A value that isn't a number shows as nothing, like formatNumberWithThousandSeparator.
export const formatMoney = (amount: number | string): string => {
  const value = amount === '' ? 0 : Number(amount);
  return Number.isFinite(value) ? moneyFormat.format(value) : '';
};

export const discountOptions = [
  { label: '%', value: 'percentage' },
  { label: 'GHS', value: 'amount' },
];

// Matches backend PhoneNumberValidator: no '+' → 10–15 digits; '+' → 11–14 digits after '+' (total ≤ 15)
export function isValidPhoneNumber(value: string): boolean {
  if (!value) return false;
  const cleaned = value.trim().replace(/[\s-]/g, '');
  if (cleaned.length > 15) return false;
  if (cleaned.startsWith('+')) {
    return /^\+\d{11,14}$/.test(cleaned);
  }
  return /^\d{10,}$/.test(cleaned);
}

// Get value from object or return default value
// Uses dot notation to access nested properties
// Example: getValue({ a: { b: 1 } }, 'a.b') => 1
// Example: getValue({ a: { b: 1 } }, 'a.c', 2) => 2
// Example: getValue({ a: { b: 1 } }, 'a.c.d') => undefined
export function getValue(obj: any, key: string, defaultValue: any = '') {
  const value = key.split('.').reduce((o, k) => o?.[k], obj);
  // console.log('value', {value});
  return value === undefined ? defaultValue : value;
};

/**
 * Sanitizes user input by removing leading and trailing whitespace.
 * Can handle both single strings and objects with multiple fields.
 * 
 * **Phone Number Sanitization:**
 * Phone number fields have ALL whitespace and dashes removed (not just trimmed).
 * This ensures consistent formatting for authentication. Users may enter phone numbers
 * in various formats like "020 123 4567", "020-123-4567", or "0201234567".
 * All formats are normalized to "0201234567" for reliable matching.
 * 
 * @param input - A string or object with string values to sanitize
 * @returns Sanitized string or object with sanitized values
 * 
 * @example
 * // Single string: trims whitespace
 * sanitizeInput('  hello  ') // returns 'hello'
 * 
 * @example
 * // Object with phone number: removes all whitespace and dashes
 * sanitizeInput({ name: '  John  ', phoneNumber: ' 020 123 4567 ' }) 
 * // returns { name: 'John', phoneNumber: '0201234567' }
 * 
 * @example
 * // Phone number with dashes
 * sanitizeInput({ phoneNumber: '020-123-4567' })
 * // returns { phoneNumber: '0201234567' }
 */
export function sanitizeInput<T extends string | Record<string, any>>(input: T): T {
  if (typeof input === 'string') {
    return input.trim() as T;
  }
  
  if (typeof input === 'object' && input !== null) {
    const sanitized: Record<string, any> = {};
    
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const value = input[key];
        
        if (typeof value === 'string') {
          // Use word boundary regex to detect phone/mobile fields precisely
          // This avoids false positives like 'smartphone', 'telephone', 'mobilehome'
          const isPhoneField = /\b(phone|mobile)\b/i.test(key);
          
          if (isPhoneField) {
            // For phone number fields, remove ALL whitespace and dashes
            sanitized[key] = value.replace(/[\s-]/g, '');
          } else {
            // For other fields, just trim leading/trailing whitespace
            sanitized[key] = value.trim();
          }
        } else {
          sanitized[key] = value;
        }
      }
    }
    
    return sanitized as T;
  }
  
  return input;
}
